import React, { useState, useEffect } from 'react'
import { api, formatDate } from '../../utils/api'
import { sourceLabel } from '../../utils/catalogReview'
import '../../styles/pages.css'

/**
 * Brand proposals: unseeded brands the mint gate is holding retailer pages for.
 * Approve seeds the brand, so the next pipeline block mints its pages. Reject
 * remembers the name, so it never comes back as pending.
 */
function CatalogBrandProposals() {
  const [proposals, setProposals] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [actionId, setActionId] = useState(null)

  useEffect(() => {
    loadProposals()
  }, [])

  async function loadProposals() {
    setLoading(true)
    setError(null)
    try {
      const response = await api.get('/api/graph/review/brand-proposals?status=pending')
      setProposals(response.items || [])
    } catch (err) {
      console.error('Failed to load brand proposals:', err)
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  async function decide(id, action) {
    setActionId(id)
    setError(null)
    try {
      await api.post(`/api/graph/review/brand-proposals/${encodeURIComponent(id)}/${action}`)
      loadProposals()
    } catch (err) {
      console.error(`Failed to ${action} brand:`, err)
      setError(err.message)
    } finally {
      setActionId(null)
    }
  }

  return (
    <>
      {error && (
        <div className="card" style={{ background: 'var(--color-error-light)', padding: 'var(--space-4)', marginBottom: 'var(--space-4)' }}>
          <strong>Error:</strong> {error}
          <button
            type="button"
            className="btn btn-sm"
            style={{ marginLeft: 'var(--space-3)' }}
            onClick={() => setError(null)}
          >
            Dismiss
          </button>
        </div>
      )}

      <div className="card" style={{ marginBottom: 'var(--space-4)' }}>
        <h3 style={{ marginTop: 0 }}>Brand proposals</h3>
        <p className="text-sm text-muted" style={{ marginTop: 0, marginBottom: 'var(--space-3)' }}>
          Unseeded brands holding retailer pages back from the mint. Approve to seed
          the brand, so the next pipeline block mints its pages. Reject to keep it out.
        </p>
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--space-12)' }}>
            <div className="loading-spinner" />
          </div>
        ) : proposals.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-title">No pending brands</div>
            <div className="empty-state-description">
              The mint gate is not holding any pages behind an unseeded brand.
            </div>
          </div>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Brand</th>
                <th>Sample title</th>
                <th>Source</th>
                <th style={{ textAlign: 'center' }}>Pages</th>
                <th style={{ width: 110 }}>First seen</th>
                <th style={{ width: 170, textAlign: 'right' }}></th>
              </tr>
            </thead>
            <tbody>
              {proposals.map(proposal => (
                <tr key={proposal.brand_proposal_id}>
                  <td style={{ fontWeight: 500 }}>{proposal.name}</td>
                  <td className="text-sm text-muted">{proposal.sample_title}</td>
                  <td className="text-sm">{sourceLabel(proposal.source)}</td>
                  <td style={{ textAlign: 'center' }}>{proposal.pages}</td>
                  <td className="text-muted text-sm">{formatDate(proposal.first_seen_at)}</td>
                  <td style={{ textAlign: 'right' }}>
                    <div style={{ display: 'flex', gap: 'var(--space-2)', justifyContent: 'flex-end' }}>
                      <button
                        type="button"
                        className="btn btn-sm btn-primary"
                        onClick={() => decide(proposal.brand_proposal_id, 'approve')}
                        disabled={actionId !== null}
                      >
                        {actionId === proposal.brand_proposal_id ? 'Working…' : 'Approve'}
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-secondary"
                        onClick={() => decide(proposal.brand_proposal_id, 'reject')}
                        disabled={actionId !== null}
                      >
                        Reject
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  )
}

export default CatalogBrandProposals
